import { define } from './define';

// The calendar: month, week and agenda views, blocked dates and weekly slots. Keys start with "calendar.".
export const calendar = define({
  es: {
    'calendar.title': 'Calendario',
    'calendar.zoneHint': 'Todas las horas están en la zona horaria de {brand}.',
    'calendar.block': 'Bloquear un día',
    'calendar.blockHint': 'Ese día no se podrá programar nada: un festivo, una crisis, un día tranquilo.',
    'calendar.blockDay': 'Día',
    'calendar.blockReason': 'Motivo (opcional)',
    'calendar.blockSubmit': 'Bloquear el día',
    'calendar.blocked': 'Bloqueado',
    'calendar.unblock': 'Desbloquear el {day}',
    'calendar.unblocked': 'Día desbloqueado',

    'calendar.prev': 'Anterior',
    'calendar.next': 'Siguiente',
    'calendar.today': 'Hoy',
    'calendar.viewLabel': 'Vista',
    'calendar.view.month': 'Mes',
    'calendar.view.week': 'Semana',
    'calendar.view.list': 'Agenda',
    'calendar.network': 'Red',
    'calendar.allNetworks': 'Todas las redes',
    'calendar.gridLabel': 'Calendario: {period}',
    'calendar.dayBlocked': '{day}, bloqueado',

    'calendar.pubs': { zero: 'Sin publicaciones', one: '{count} publicación', other: '{count} publicaciones' },
    'calendar.freeSlots': { one: '{count} hueco libre', other: '{count} huecos libres' },
    'calendar.more': '+{count} más',
    'calendar.moreLabel': 'Ver los {count} restantes en la semana',
    'calendar.slot': 'Hueco',
    'calendar.slotTitle': 'Hueco libre: {label} · {account}',
    'calendar.byHand': 'a mano',
    'calendar.automatic': 'automática',
    'calendar.moved': 'Movida al {day}',
    'calendar.dragHint': 'Arrastra una publicación a otro día para moverla (conserva su hora), o una pieza de «Sin programar» a un día para programarla.',
    'calendar.dropHere': 'Suelta aquí',
    'calendar.todayEmpty': 'Hoy no sale nada.',

    'calendar.tray.title': 'Sin programar',
    'calendar.tray.hint': 'Arrastra una pieza a un día para programarla, o haz clic para elegir cuándo.',
    'calendar.tray.hintTap': 'Toca una pieza para elegir cuándo sale.',
    'calendar.tray.empty': 'Todo lo aprobado tiene fecha.',
    'calendar.tray.scheduleLabel': 'Programar «{title}»',
    'calendar.tray.paused': 'Con la marca en pausa no se puede programar.',
    'calendar.tray.show': 'Mostrar lo aprobado sin programar',
    'calendar.tray.hide': 'Ocultar lo aprobado sin programar',
    'calendar.paused': 'En pausa',
    'calendar.pausedHint': 'Con la marca en pausa no sale nada de lo programado y no se puede mover.',

    'calendar.legend': 'Leyenda',
    'calendar.legend.slot': 'Hueco libre',
    'calendar.legend.blocked': 'Día bloqueado',

    'calendar.empty': 'Nada en estas fechas.',
    'calendar.live': { zero: 'Sin publicaciones entre el {from} y el {to}', one: '{count} publicación entre el {from} y el {to}', other: '{count} publicaciones entre el {from} y el {to}' },

    // Network short names, in the monospaced tags of the calendar, the publish page and the results.
    'calendar.net.instagram': 'IG',
    'calendar.net.facebook': 'FB',
    'calendar.net.youtube': 'YT',
    'calendar.net.tiktok': 'TT',
    'calendar.net.linkedin': 'LI',
    'calendar.net.x': 'X',
    'calendar.net.threads': 'TH',
    'calendar.net.pinterest': 'PI',
    'calendar.net.bluesky': 'BS',
  },
  en: {
    'calendar.title': 'Calendar',
    'calendar.zoneHint': 'Every time is in the time zone of {brand}.',
    'calendar.block': 'Block a date',
    'calendar.blockHint': 'Nothing can be scheduled on a blocked date: a bank holiday, a crisis, a quiet day.',
    'calendar.blockDay': 'Date',
    'calendar.blockReason': 'Reason (optional)',
    'calendar.blockSubmit': 'Block date',
    'calendar.blocked': 'Blocked',
    'calendar.unblock': 'Unblock {day}',
    'calendar.unblocked': 'Date unblocked',

    'calendar.prev': 'Previous',
    'calendar.next': 'Next',
    'calendar.today': 'Today',
    'calendar.viewLabel': 'View',
    'calendar.view.month': 'Month',
    'calendar.view.week': 'Week',
    'calendar.view.list': 'List',
    'calendar.network': 'Network',
    'calendar.allNetworks': 'All networks',
    'calendar.gridLabel': 'Calendar: {period}',
    'calendar.dayBlocked': '{day}, blocked',

    'calendar.pubs': { zero: 'No posts', one: '{count} post', other: '{count} posts' },
    'calendar.freeSlots': { one: '{count} empty slot', other: '{count} empty slots' },
    'calendar.more': '+{count} more',
    'calendar.moreLabel': 'See the other {count} in the week view',
    'calendar.slot': 'Slot',
    'calendar.slotTitle': 'Empty slot: {label} · {account}',
    'calendar.byHand': 'by hand',
    'calendar.automatic': 'automatic',
    'calendar.moved': 'Moved to {day}',
    'calendar.dragHint': 'Drag a post to another day to move it (it keeps its time), or a piece from “Not scheduled” onto a day to schedule it.',
    'calendar.dropHere': 'Drop here',
    'calendar.todayEmpty': 'Nothing goes out today.',

    'calendar.tray.title': 'Not scheduled',
    'calendar.tray.hint': 'Drag a piece onto a day to schedule it, or click to choose when.',
    'calendar.tray.hintTap': 'Tap a piece to choose when it goes out.',
    'calendar.tray.empty': 'Everything approved has a date.',
    'calendar.tray.scheduleLabel': 'Schedule “{title}”',
    'calendar.tray.paused': 'While the brand is paused nothing can be scheduled.',
    'calendar.tray.show': 'Show what is approved and not scheduled',
    'calendar.tray.hide': 'Hide what is approved and not scheduled',
    'calendar.paused': 'Paused',
    'calendar.pausedHint': 'While the brand is paused nothing scheduled goes out and nothing can be moved.',

    'calendar.legend': 'Legend',
    'calendar.legend.slot': 'Empty slot',
    'calendar.legend.blocked': 'Blocked day',

    'calendar.empty': 'Nothing on these dates.',
    'calendar.live': { zero: 'No posts between {from} and {to}', one: '{count} post between {from} and {to}', other: '{count} posts between {from} and {to}' },

    'calendar.net.instagram': 'IG',
    'calendar.net.facebook': 'FB',
    'calendar.net.youtube': 'YT',
    'calendar.net.tiktok': 'TT',
    'calendar.net.linkedin': 'LI',
    'calendar.net.x': 'X',
    'calendar.net.threads': 'TH',
    'calendar.net.pinterest': 'PI',
    'calendar.net.bluesky': 'BS',
  },
});
